import { useId, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Input } from "./Input";
import { Label } from "./Label";

export interface DangerConfirmationCredentials {
  password: string;
  totpCode: string;
}

export interface DangerConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (credentials?: DangerConfirmationCredentials) => Promise<void> | void;
  title: string;
  description?: string;
  /** Bullet list of concrete consequences shown to the operator before they can confirm. */
  consequences: string[];
  /** Exact phrase the operator must type to enable the confirm button (e.g. the target mode). */
  confirmationPhrase: string;
  confirmLabel?: string;
  credentialsRequired?: boolean;
}

/**
 * Strong, two-step confirmation for irreversible or highly disruptive actions
 * (app modes maintenance / force_update / disabled). Never a simple switch:
 * the operator must read the consequences and retype an exact phrase before
 * the action becomes available, and is told the backend may require
 * reauthentication or a TOTP code to actually apply it.
 */
export function DangerConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  consequences,
  confirmationPhrase,
  confirmLabel = "Aplicar cambio",
  credentialsRequired = false,
}: DangerConfirmDialogProps) {
  const [typedValue, setTypedValue] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();
  const passwordId = useId();
  const totpId = useId();

  const canConfirm =
    typedValue.trim() === confirmationPhrase &&
    (!credentialsRequired || (password.length > 0 && totpCode.trim().length >= 6));

  async function handleConfirm() {
    if (!canConfirm) return;
    setLoading(true);
    setError(null);
    try {
      await onConfirm(credentialsRequired ? { password, totpCode: totpCode.trim() } : undefined);
      handleClose();
    } catch (caught) {
      const message = caught instanceof Error && caught.message
        ? caught.message
        : "No se pudo completar la operacion.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setTypedValue("");
    setPassword("");
    setTotpCode("");
    setError(null);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      description={description}
      size="sm"
      preventDismiss={loading}
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={loading}
            disabled={!canConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-danger-200 bg-danger-50 p-3">
          <ShieldAlert className="h-5 w-5 shrink-0 text-danger-600" aria-hidden="true" />
          <div className="space-y-1.5 text-sm text-danger-900">
            <p className="font-medium">Esta accion afecta a IESAPP en produccion.</p>
            <ul className="list-disc space-y-1 pl-4">
              {consequences.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>
              El backend puede exigir reautenticacion o un codigo TOTP para completar
              esta operacion.
            </p>
          </div>
        </div>

        {credentialsRequired && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor={passwordId}>Contrasena administrativa</Label>
              <Input
                id={passwordId}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={totpId}>Codigo TOTP</Label>
              <Input
                id={totpId}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                autoComplete="one-time-code"
                disabled={loading}
                className="text-center text-lg tracking-[0.3em]"
              />
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor={inputId}>
            Escribe <span className="font-mono font-semibold">{confirmationPhrase}</span>{" "}
            para confirmar
          </Label>
          <Input
            id={inputId}
            value={typedValue}
            onChange={(e) => setTypedValue(e.target.value)}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-describedby={`${inputId}-hint`}
          />
          <p id={`${inputId}-hint`} className="text-xs text-ink-faint">
            Esto evita cambios accidentales de estado en la aplicacion.
          </p>
        </div>

        {error && (
          <div role="alert" className="rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-900">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
